// 15-minute inactivity logout — same threshold dashboard-pro uses. On idle
// we call authClient.logout() which the @mf/auth-client owns: it POSTs
// /api/auth/logout and navigates the browser to the B2C end-session URL
// (typed `Promise<never>` because it doesn't resolve — it does the redirect).
// If the call rejects we still want to bounce the user away from the
// authenticated surface, so we send them to the neutral product home
// (altersvorsorge.{brand}) — not back into a protected/forced-signin surface.

import { useIdleTimer } from "react-idle-timer";
import { useEffect, useRef } from "react";
import { authClient } from "./auth";
import { resolveBrand, surfaceUrl } from "./brand";

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;

interface IdleLogoutProps {
  enabled?: boolean;
  // Test seam — overrides the default authClient.logout() call.
  onLogout?: () => Promise<unknown>;
}

export function IdleLogout({ enabled = true, onLogout }: IdleLogoutProps) {
  // Guard against double-firing during the redirect window: react-idle-timer
  // can fire onIdle twice if the user stays away long enough.
  const firingRef = useRef(false);

  useIdleTimer({
    timeout: FIFTEEN_MINUTES_MS,
    crossTab: true,
    disabled: !enabled,
    onIdle: () => {
      if (firingRef.current) return;
      firingRef.current = true;
      const logout = onLogout ?? ((): Promise<unknown> => authClient.logout());
      logout().catch(() => {
        // Adapter unreachable — fall back to a hard redirect so the
        // signed-in UI doesn't linger. Neutral product home, not a protected
        // surface (which would immediately force re-signin).
        const brand = resolveBrand();
        window.location.href = surfaceUrl("altersvorsorge", brand) + "/";
      });
    },
  });

  // Reset the firing latch when the component unmounts (e.g. SPA navigates
  // back to a logged-out surface).
  useEffect(() => {
    return () => {
      firingRef.current = false;
    };
  }, []);

  return null;
}
